import React from 'react';
import RowHeader from './RowHeader';
import ListingCard from './ListingCard';
import { HSCROLL } from './tokens';
import { savedKeyFromView, type HomeListingView } from './listingView';
import type { SavedTarget } from '../../../lib/api/savedTargets';

/**
 * HOME-01 — Spotlight homes.
 *
 * The first surface Spotlight has ever had on the home page. Spotlight is a
 * paid 7-day boost (KES 2,000) that buys placement in THIS carousel and a badge
 * — nothing else. It does not touch main-feed ranking, and the rows below this
 * one are ordered by SYND-10's assembler exactly as they were before.
 *
 * SYNDICATED ROWS ARE EXCLUDED, structurally rather than by a filter: the only
 * sources of this list are `getSpotlightListings()` and the SSR spotlight query,
 * both of which read the `listings` table alone. `syndicated_listings` has no
 * `spotlight_until` column and no way to acquire one — Spotlight is a product
 * an Afriqahome agent buys, and an indexed announcement has no agent to buy it.
 *
 * COLLAPSES ENTIRELY when there is nothing to show. Not an empty shell, not a
 * "no spotlights yet" state: a paid placement row with nothing in it advertises
 * that nobody bought the placement.
 */

interface SpotlightRowProps {
  listings: HomeListingView[];
  /** SYND-15: keyed `${kind}:${id}` — see savedKeyFromView, never a bare uuid. */
  savedIds: ReadonlySet<string>;
  /** SYND-15: the card composes a SavedTarget from its own `source`. */
  onSave: (target: SavedTarget, e: React.MouseEvent) => void;
  /** Omitted → the header carries no "View all" link rather than a dead one. */
  viewAllHref?: string;
  className?: string;
}

const SpotlightRow: React.FC<SpotlightRowProps> = ({
  listings,
  savedIds,
  onSave,
  viewAllHref,
  className = '',
}) => {
  if (listings.length === 0) return null;

  return (
    <section className={`pt-5 lg:pt-9 ${className}`} aria-labelledby="home-spotlight">
      <RowHeader
        title="Spotlight homes"
        tooltip="Boosted by the agent for 7 days"
        viewAllHref={viewAllHref}
        level="section"
        className="mb-2.5 lg:mb-4"
      />
      <div className={HSCROLL}>
        {listings.map((listing) => (
          <ListingCard
            /* SYND-15: kind-prefixed, like every other key over these two id
               spaces (unifiedResultKey, savedIds). This row is marketplace-only
               — Spotlight is a placement an agent buys, and an indexed row has
               no agent to buy it — so this changes nothing here; it is here so
               that the key rule is the same in every row, including New this
               week, which holds both kinds since SYND-18. */
            key={savedKeyFromView(listing)}
            listing={listing}
            variant="spotlight"
            saved={savedIds.has(savedKeyFromView(listing))}
            onSave={onSave}
          />
        ))}
      </div>
    </section>
  );
};

export default SpotlightRow;
